/**
 * Phase Completion Helper
 *
 * Calculates which phases are complete based on company data.
 * Mirrors the backend logic in Company.cs
 */

export interface CompanyPhaseStatus {
  isPhase1Complete: boolean;
  isPhase2Complete: boolean;
  isPhase3Complete: boolean;
  firstIncompletePhase: number;
}

/**
 * Checks if Phase 1 (Partners) has all required fields completed
 */
function isPhase1Complete(company: any): boolean {
  if (!company.partners || !Array.isArray(company.partners) || company.partners.length === 0) {
    return false;
  }

  // All partners must have required fields: name, cpf, phone
  return company.partners.every((partner: any) =>
    partner.name?.trim() &&
    partner.cpf?.trim() &&
    partner.phone?.trim()
  );
}

/**
 * Checks if Phase 2 (Company Data) has all required fields completed
 */
function isPhase2Complete(company: any): boolean {
  // Phase 2 requires: CNPJ, Name, TaxRegime, CnaePrincipal, Segmento, RamoAtividadeId
  const hasBasicFields =
    company.cnpj &&
    company.name &&
    company.name !== 'TEMPORARIO' &&
    company.taxRegime != null &&
    company.taxRegime !== 0 && // TaxRegime.None
    company.cnaePrincipal &&
    company.cnaePrincipal !== 'TEMPORARIO' &&
    company.ramoAtividadeId;

  // Segmento is required for all tax regimes
  const hasSegmento = !!company.segmento;

  // Anexo is required for Simples Nacional (1)
  const hasAnexoIfRequired =
    company.taxRegime !== 1 || // Not Simples Nacional
    !!company.anexo; // Or has Anexo

  return hasBasicFields && hasSegmento && hasAnexoIfRequired;
}

/**
 * Checks if Phase 3 (Certificates) has all required fields completed
 */
function isPhase3Complete(company: any): boolean {
  // Phase 3 is optional - certificates can be added later
  // Consider it complete if at least one certificate is present
  return !!company.certificate;
}

/**
 * Gets the first incomplete phase number for navigation (1-4)
 *
 * Note: For Abertura workflow:
 * - Backend Phase 1 → Frontend Phase 1 (Sócios)
 * - Backend Phase 2 → Frontend Phase 4 (Dados da Empresa)
 * - Backend Phase 3 → Frontend Phase 2 (Certificados/Documents)
 * - Backend Phase 4 → Frontend Phase 3 (Check List)
 */
export function getCompanyPhaseStatus(company: any): CompanyPhaseStatus {
  const phase1Complete = isPhase1Complete(company);
  const phase2Complete = isPhase2Complete(company);
  const phase3Complete = isPhase3Complete(company);

  let firstIncompletePhase: number;

  if (!phase1Complete) {
    firstIncompletePhase = 1;
  } else if (!phase2Complete) {
    firstIncompletePhase = 2; // For Portabilidade: Phase 2. For Abertura: maps to Frontend Phase 4
  } else if (!phase3Complete) {
    firstIncompletePhase = 3; // For Portabilidade: Phase 3. For Abertura: maps to Frontend Phase 2
  } else {
    firstIncompletePhase = 4; // All complete
  }

  return {
    isPhase1Complete: phase1Complete,
    isPhase2Complete: phase2Complete,
    isPhase3Complete: phase3Complete,
    firstIncompletePhase,
  };
}

/**
 * Maps backend phase number to frontend phase number for Abertura workflow
 */
export function mapBackendPhaseToFrontend(backendPhase: number, registrationType: string): number {
  if (registrationType?.toLowerCase() === 'abertura') {
    // Abertura has different phase mapping
    switch (backendPhase) {
      case 1: return 1; // Sócios
      case 2: return 4; // Dados da Empresa
      case 3: return 2; // Certificados/Documents
      case 4: return 3; // Check List / Completed
      default: return backendPhase;
    }
  }

  // Portabilidade uses direct mapping
  return backendPhase;
}
